import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { coursesAPI } from '../../services/api'
import type { Course, CourseEnrollment } from '../../types'
import { CATALOG_PATH, coursePath } from './courseUtils'

type Taking = { enrollment: CourseEnrollment; course: Course }

/** The courses you're taking (or finished), with your progress; or the way to find one. */
const MyLearning = () => {
  const [taking, setTaking] = useState<Taking[] | null>(null)

  useEffect(() => {
    coursesAPI
      .myEnrollments()
      .then(async (list) => {
        const mine = list.filter((e) => e.status === 'active' || e.status === 'completed')
        const found = await Promise.all(
          mine.map((enrollment) =>
            coursesAPI
              .get(enrollment.course_id)
              .then((course) => ({ enrollment, course }))
              .catch(() => null)
          )
        )
        setTaking(found.filter((t): t is Taking => t !== null))
      })
      .catch(() => setTaking([]))
  }, [])

  if (taking === null) return null
  return (
    <section className="rounded-lg bg-white p-5 shadow" aria-labelledby="my-learning" data-testid="my-learning">
      <h2 id="my-learning" className="font-semibold text-gray-900">
        Courses you&rsquo;re taking
      </h2>
      {taking.length === 0 ? (
        <p className="mt-1 text-sm text-gray-600">
          None yet.{' '}
          <Link to={CATALOG_PATH} className="text-primary-700 underline">
            Find a course
          </Link>
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-gray-100">
          {taking.map(({ enrollment, course }) => (
            <li key={enrollment.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
              <Link to={coursePath(course.slug)} className="font-medium text-gray-900 hover:text-primary-700">
                {course.title}
              </Link>
              <span className="text-xs text-gray-500">
                {enrollment.status === 'completed' ? 'Completed' : `${Math.round(enrollment.progress_percentage)}% done`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export default MyLearning
